import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import { Button, Input, Sheet, SheetBody, SheetFooter, SheetForm, SheetHeader, Textarea } from '@/features/common/ui';

export interface VendorRecord {
  id: string;
  displayName: string;
  companyName: string | null;
  email: string | null;
  phone: string | null;
  gstin: string | null;
  pan: string | null;
  notes: string | null;
}

interface Form {
  displayName: string;
  companyName: string;
  email: string;
  phone: string;
  gstin: string;
  pan: string;
  notes: string;
}

const EMPTY: Form = { displayName: '', companyName: '', email: '', phone: '', gstin: '', pan: '', notes: '' };

/**
 * A vendor - who you pay - added or edited.
 *
 * Not the customer sheet: a vendor is never invoiced, so the GST treatment,
 * place of supply, payment terms and portal that sheet carries are questions
 * with no answer here. What a vendor needs is who they are and the two tax
 * numbers their bills carry, which is what decides whether input credit and
 * TDS apply.
 */
export function VendorSheet({
  vendorId,
  initialName,
  onClose,
  onSaved,
}: {
  /** Absent: a new vendor. */
  vendorId?: string;
  /** Prefilled on a new one - "Add vendor" from an expense's payee. */
  initialName?: string;
  onClose: () => void;
  onSaved: (vendor: VendorRecord) => void;
}) {
  useEscapeToClose(onClose);
  const org = useActiveOrg();
  const editing = !!vendorId;
  const [form, setForm] = useState<Form>({ ...EMPTY, displayName: initialName ?? '' });

  const existing = useQuery({
    queryKey: ['vendors', org?.id, 'one', vendorId],
    queryFn: () => api.get<VendorRecord>(`/vendors/${vendorId}`),
    enabled: editing && !!org?.id,
  });
  useEffect(() => {
    const v = existing.data;
    if (!v) return;
    setForm({
      displayName: v.displayName ?? '',
      companyName: v.companyName ?? '',
      email: v.email ?? '',
      phone: v.phone ?? '',
      gstin: v.gstin ?? '',
      pan: v.pan ?? '',
      notes: v.notes ?? '',
    });
  }, [existing.data]);

  const payload = () => ({
    displayName: form.displayName.trim(),
    companyName: form.companyName.trim() || undefined,
    email: form.email.trim(),
    phone: form.phone.trim() || undefined,
    gstin: form.gstin.trim(),
    pan: form.pan.trim(),
    // A GSTIN is what makes their bills carry input credit.
    gstTreatment: form.gstin.trim() ? 'business_gst' : 'business_none',
    notes: form.notes.trim() || undefined,
  });

  const save = useMutation({
    mutationFn: () => (editing ? api.patch<VendorRecord>(`/vendors/${vendorId}`, payload()) : api.post<VendorRecord>('/vendors', payload())),
    onSuccess: (vendor) => onSaved(vendor),
  });
  const error = save.error instanceof ApiError ? save.error : undefined;
  const set = (key: keyof Form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));
  const title = editing ? `Edit ${existing.data?.displayName ?? 'vendor'}` : 'New vendor';

  return (
    <Sheet label={title} onClose={onClose} testId="vendor-sheet">
      <SheetHeader title={title} onClose={onClose} />
      <SheetForm
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <SheetBody>
          {editing && existing.isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : null}
          <Input label="Name" required autoFocus={!editing} value={form.displayName} onChange={set('displayName')} error={error?.fieldError('displayName')} hint="As you know them - Hostinger, the landlord, Albi Maria." />
          <Input label="Company name" value={form.companyName} onChange={set('companyName')} error={error?.fieldError('companyName')} hint="The legal name on their bills, when it differs." />
          <Input label="GSTIN" value={form.gstin} onChange={set('gstin')} error={error?.fieldError('gstin')} hint="On their bills. With it, the GST they charge you is input credit." />
          <Input label="PAN" value={form.pan} onChange={set('pan')} error={error?.fieldError('pan')} hint="For TDS, when you deduct it from what you pay them." />
          <Input label="Email" type="email" value={form.email} onChange={set('email')} error={error?.fieldError('email')} />
          <Input label="Phone" value={form.phone} onChange={set('phone')} error={error?.fieldError('phone')} />
          <Textarea label="Notes" value={form.notes} onChange={set('notes')} rows={3} />
          {error && !error.errors?.length ? (
            <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error.message}
            </p>
          ) : null}
        </SheetBody>
        <SheetFooter>
          <Button type="submit" loading={save.isPending} disabled={editing && existing.isLoading} data-testid="vendor-save">
            {editing ? 'Save changes' : 'Add vendor'}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}
